import SearchBox from '../components/SearchBox';
import Highlight from '../components/Highlight';
import { searchTerms, matchesAllTerms } from '../utils/search';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import TemplatePreview from '../components/TemplatePreview';
import { PageHeading, NtIcon } from '../components/NotionUI';
import { getProjectTemplates, applyProjectTemplate, getProjects, errorMessage } from '../services/api';
import { setProjectMenu } from '../projectMenu';
import { useLanguage } from '../i18n/LanguageContext';
import { resolveLabel } from '../utils/fieldLabels';
import { TYPE_ICONS } from '../utils/projectView';

// Raggruppamento dei modelli per ambito (quelli non elencati finiscono in "Altro").
const CATEGORY_OF = {
  support: 'support', it_helpdesk: 'support', bug_report: 'support', purchase_requests: 'support', maintenance: 'support', internal_requests: 'support',
  goals: 'planning', campaigns: 'planning', content_calendar: 'planning', events: 'planning', social_planner: 'planning',
  issue_tracking: 'development', feature_requests: 'development', tech_docs: 'development', product_roadmap: 'development',
  fundraising: 'sales', sales_pipeline: 'sales', account_management: 'sales',
};
const CATEGORIES = ['support', 'planning', 'development', 'sales', 'other'];
const categoryOf = (tpl) => CATEGORY_OF[tpl.id] || 'other';

function ProjectTemplates({ user, onLogout }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  useEffect(() => {
    getProjectTemplates()
      .then((d) => setTemplates(d.templates || []))
      .catch((err) => setError(errorMessage(err, t('projectTemplates.loadError'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = (labels) => resolveLabel(labels, lang, 'it-IT');

  // Più parole separate da spazio: devono comparire tutte (AND), nel nome o nella descrizione.
  const terms = searchTerms(query);
  const matches = (tpl) => matchesAllTerms(`${label(tpl.name)} ${label(tpl.description)}`, terms);
  const searched = templates.filter(matches);
  const counts = Object.fromEntries(CATEGORIES.map((c) => [c, searched.filter((tpl) => categoryOf(tpl) === c).length]));
  const visible = searched.filter((tpl) => category === 'all' || categoryOf(tpl) === category);

  const choose = (tpl) => {
    setSelected(tpl);
    setName(label(tpl.name));
    setError('');
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const data = await applyProjectTemplate(selected.id, { name, lang });
      setProjectMenu((await getProjects()).projects || []);
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      setError(errorMessage(err, t('projectTemplates.applyError')));
      setCreating(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page">
        <PageHeading icon="layout" title={t('projectTemplates.title')} subtitle={t('projectTemplates.subtitle')} />

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : selected ? (
          <>
            <button type="button" className="tpl-back" onClick={() => setSelected(null)} disabled={creating}>
              <NtIcon name="back" /> {t('projectTemplates.backToAll')}
            </button>
            <div className="tpl-detail-layout">
              <div className="template-detail" style={{ '--tpl-color': selected.color }}>
                <div className="tpl-head">
                  <span className="tpl-icon" aria-hidden="true">{selected.icon}</span>
                  <div>
                    <h2 className="template-detail-title">{label(selected.name)}</h2>
                    <span className="template-card-desc">{label(selected.description)}</span>
                  </div>
                </div>
                <TemplatePreview template={selected} lang={lang} large hideTitle />
              </div>
              <aside className="tpl-side">
                <form onSubmit={handleApply} className="tpl-form">
                  <div className="form-group">
                    <label>{t('projectTemplates.projectName')}</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
                  </div>
                  <div className="tpl-facts">
                    <div className="tpl-facts-title">{t('projectTemplates.columnsIncluded', { count: selected.columns.length })}</div>
                    <div className="tpl-chips">
                      {selected.columns.map((column) => (
                        <span key={column.key} className="tpl-chip">
                          <NtIcon name={TYPE_ICONS[column.type] || 'text'} size={13} /> {label(column.name)}
                        </span>
                      ))}
                    </div>
                    <div className="tpl-facts-title">{t('projectTemplates.sampleTasks', { count: selected.tasks.length })}</div>
                    <ul className="tpl-tasks">
                      {selected.tasks.map((task, i) => <li key={i}>{label(task.title)}</li>)}
                    </ul>
                  </div>
                  <button type="submit" className="btn tpl-create" disabled={creating || !name.trim()}>
                    {creating ? t('projectTemplates.creating') : t('projectTemplates.createProject')}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)} disabled={creating}>
                    {t('projectTemplates.cancel')}
                  </button>
                </form>
              </aside>
            </div>
          </>
        ) : (
          <>
            <div className="tpl-filters">
              <SearchBox value={query} onChange={setQuery} placeholder={t('projectTemplates.search')} title={t('projectTemplates.searchHint')} />
              <div className="tpl-cats" role="group" aria-label={t('projectTemplates.categories')}>
                <button type="button" className={'tpl-cat' + (category === 'all' ? ' tpl-cat-active' : '')} onClick={() => setCategory('all')}>
                  {t('projectTemplates.cat_all')} <span className="tpl-cat-count">{searched.length}</span>
                </button>
                {CATEGORIES.filter((c) => counts[c] > 0).map((c) => (
                  <button key={c} type="button" className={'tpl-cat' + (category === c ? ' tpl-cat-active' : '')} onClick={() => setCategory(c)}>
                    {t(`projectTemplates.cat_${c}`)} <span className="tpl-cat-count">{counts[c]}</span>
                  </button>
                ))}
              </div>
            </div>
            {visible.length === 0 ? (
              <p className="nt-empty">{t('projectTemplates.noResults')}</p>
            ) : (
              <div className="template-grid">
                {visible.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    className="template-card"
                    style={{ '--tpl-color': tpl.color }}
                    onClick={() => choose(tpl)}
                  >
                    <span className="tpl-head">
                      <span className="tpl-icon" aria-hidden="true">{tpl.icon}</span>
                      <span className="tpl-head-text">
                        <span className="template-card-title"><Highlight text={label(tpl.name)} terms={terms} /></span>
                        <span className="template-card-desc"><Highlight text={label(tpl.description)} terms={terms} /></span>
                      </span>
                    </span>
                    <TemplatePreview template={tpl} lang={lang} hideTitle />
                    <span className="tpl-use">{t('projectTemplates.useTemplate')} →</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default ProjectTemplates;
